import React, { ChangeEvent, useCallback, useState } from 'react';
import {
    Button,
    FormControl,
    FormLabel,
    Input,
    HStack,
    VStack,
    Textarea,
    FormErrorMessage,
} from '@chakra-ui/react';
import { TopicObject } from '.';

interface Props {
    onSaveAction: (formData: TopicObject) => void;
    onCancelAction: () => void;
    editableTopic: TopicObject | undefined;
}

function TopicForm(props: Props) {
    const {
        onSaveAction,
        onCancelAction,
        editableTopic,
    } = props;

    const [name, setName] = useState<string>(editableTopic?.name ?? '');
    const [description, setDescription] = useState<string>(editableTopic?.description ?? '');

    const [nameError, setNameError] = useState('');

    const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (nameError) {
            setNameError('');
            return;
        }
        setName(e.target.value);
    }, [nameError]);

    const handleDescriptionChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value);
    }, []);

    const onClearForm = useCallback(
        () => {
            setName('');
            setDescription('');
        },
        [],
    );

    const onCancelSave = useCallback(
        () => {
            onClearForm();
            onCancelAction();
        },
        [onCancelAction, onClearForm],
    );

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            if (!name) {
                setNameError('Please enter name');
                return;
            }
            onSaveAction({
                name,
                description,
            } as TopicObject);
            onClearForm();
        },
        [description, name, onSaveAction, onClearForm],
    );

    return (
        <form onSubmit={handleSubmit}>
            <VStack
                spacing={4}
                borderWidth={1}
                padding={4}
            >
                <FormControl isInvalid={!!nameError}>
                    <FormLabel
                        htmlFor="name"
                        width="10%"
                    >
                        Name *
                    </FormLabel>
                    <Input
                        id="name"
                        value={name}
                        onChange={handleNameChange}
                        placeholder="Topic Name"
                        background="whiteAlpha.900"
                        isRequired
                        autoComplete="off"
                        minHeight={12}
                    />
                    {nameError && (
                        <FormErrorMessage>
                            {nameError}
                        </FormErrorMessage>
                    )}
                </FormControl>
                <FormControl>
                    <FormLabel htmlFor="description">
                        Description
                    </FormLabel>
                    <Textarea
                        id="description"
                        value={description}
                        onChange={handleDescriptionChange}
                        placeholder="Topic description"
                        rows={3}
                        autoComplete="off"
                    />
                </FormControl>
                <HStack
                    width="100%"
                    justifyContent="flex-end"
                    spacing={4}
                >
                    <Button
                        type="reset"
                        colorScheme="brand"
                        variant="outline"
                        letterSpacing={1}
                        onClick={onClearForm}
                    >
                        Clear Form
                    </Button>
                    <Button
                        type="reset"
                        colorScheme="brand"
                        variant="outline"
                        letterSpacing={1}
                        onClick={onCancelSave}
                        py={4}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        letterSpacing={1}
                        colorScheme="brand"
                        py={4}
                    >
                        {`${editableTopic ? 'Update' : 'Submit'}`}
                    </Button>
                </HStack>
            </VStack>
        </form>
    );
}
export default TopicForm;
